import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Image,
  StyleSheet,
  Text,
  TextInput,
  TouchableHighlight,
  View,
} from './components/base_components';
import { useBusy, BusyOverlay } from './components/busy_component';
import InlineTextButton from './components/buttons/inline_text_button';
import TextButton from './components/buttons/text_button';

import UserStore from './stores/user_store';

const styles = StyleSheet.create({
  loginScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'var(--login-bg-color)',
  },
  topLine: {
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    marginBottom: '2rem',
    width: '25rem',
    height: '5rem',
    filter: 'var(--icon-black)',
  },
  boxView: {
    alignSelf: 'stretch',
    margin: '2rem',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  box: {
    flex: 1,
    maxWidth: '40rem',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  circleView: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 0,
    height: 0,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'visible',
  },
  circles: {
    width: '50rem',
    height: '50rem',
    filter: 'var(--login-circle-color)',
    maskImage: 'radial-gradient(white, transparent)',
  },
  lines: {
    width: '50rem',
    height: '50rem',
    filter: 'var(--login-circle-color)',
    maskImage: 'radial-gradient(white, transparent)',
  },
  title: {
    color: 'var(--login-title-text)',
    fontSize: '3rem',
    fontWeight: '600',
  },
  subtitle: {
    marginTop: '1.5rem',
    color: 'var(--login-subtitle-text)',
    fontSize: '1.6rem',
    textAlign: 'center',
  },
  email: {
    marginTop: '3rem',
    alignSelf: 'stretch',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '4rem',
    color: 'var(--login-input-text)',
    fontSize: '1.5rem',
    borderRadius: '0.5rem',
    borderWidth: 1,
    borderColor: 'var(--login-input-border)',
    backgroundColor: 'var(--login-input-bg)',
  },
  line: {
    marginTop: '1rem',
    flexDirection: 'row',
  },
  middleSpace: {
    height: '3rem',
  },
  button: {
    marginTop: '1.5rem',
    alignSelf: 'stretch',
  },
  resendText: {
    color: 'var(--login-left-text)',
    fontSize: '1.4rem',
    fontWeight: 400,
  },
  resendLink: {
    marginLeft: '1rem',
    color: 'var(--link-text-color)',
    fontSize: '1.5rem',
    fontWeight: '400',
    textDecoration: 'underline',
  },
  highlight: {
    height: '3.5rem',
    borderRadius: '1rem',
    overflow: 'hidden',
  },
  highlightInner: {
    paddingLeft: '2rem',
    paddingRight: '2rem',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  left: {
    marginRight: '1rem',
    width: '1.5rem',
    height: '1.5rem',
    filter: 'var(--login-left-color)',
  },
  leftText: {
    color: 'var(--login-left-text)',
    fontSize: '1.4rem',
    fontWeight: '600',
  },
});

const CIRCLES = require('./assets/img/circles.svg');
const CODE = require('./assets/img/email_code.svg');
const LINES = require('./assets/img/lines.svg');
const LOGO = require('./assets/img/logo.png');
const LEFT = require('./assets/img/arrow_left_black.svg');

export default function LoginScreen() {
  const [email, setEmail] = React.useState('');
  const [login_code, setLoginCode] = React.useState('');
  const [is_busy, setBusy, clearBusy] = useBusy();
  const [show_code, setShowCode] = React.useState(false);
  const [show_not_member, setShowNotMember] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dest =
    (location?.state?.from === '/logout' ? '/' : location?.state?.from) ?? '/';

  function _onSendCode() {
    if (email && setBusy()) {
      UserStore.emailCode({ email }, (err) => {
        clearBusy();
        if (err === 404) {
          window.alert('User not found.  Please signup before logging in.');
        } else if (err) {
          window.alert('Failed to send code.');
        } else {
          setShowCode(true);
        }
      });
    }
  }
  function _onSignin() {
    if (email && login_code && setBusy()) {
      UserStore.loginCode({ email, login_code }, (err) => {
        if (err === 'not_admin') {
          window.alert('Sorry, not authorized.');
          _onLoginCancel();
        } else if (err) {
          window.alert('Login failed, please check your code.');
        } else {
          navigate(dest);
        }
        clearBusy();
      });
    }
  }
  function _onLoginCancel() {
    setEmail('');
    setLoginCode('');
    setShowCode(false);
    setShowNotMember(false);
  }
  let content: any = null;
  if (show_not_member) {
    content = (
      <View style={styles.box}>
        <Text style={styles.title}>Not A Member</Text>
        <Text style={styles.subtitle}>
          You're not a member of this Organization. Please contact your
          administrator and have him invite you to this organization.
        </Text>
        <View style={styles.middleSpace} />
        <TouchableHighlight
          style={styles.highlight}
          underlayColor='rgba(0,0,0,0.1)'
          onPress={_onLoginCancel}
        >
          <View style={styles.highlightInner}>
            <Image style={styles.left} source={LEFT} />
            <Text style={styles.leftText}>Back to log in</Text>
          </View>
        </TouchableHighlight>
      </View>
    );
  } else if (show_code) {
    content = (
      <View style={styles.box}>
        <View style={styles.topLine}>
          <View style={styles.circleView}>
            <Image style={styles.lines} source={LINES} />
          </View>
          <Image style={styles.logo} source={CODE} />
        </View>
        <Text style={styles.title}>Check your email</Text>
        <Text style={styles.subtitle}>
          We sent a verification code to {email}
        </Text>
        <TextInput
          style={styles.email}
          placeholder='code'
          value={login_code}
          onChangeText={setLoginCode}
          autoFocus={true}
          inputMode='numeric'
          autoCapitalize='off'
          autoCorrect='off'
          onSubmitEditing={_onSignin}
        />
        <TextButton
          style={styles.button}
          type='blue'
          text='Verify email'
          onPress={_onSignin}
        />
        <View style={styles.middleSpace} />
        <View style={styles.line}>
          <Text style={styles.resendText}>Didn't receive the email?</Text>
          <InlineTextButton
            style={styles.resendLink}
            underlayColor='rgba(0,0,0,0.1)'
            text='Click to resend'
            onPress={_onSendCode}
          />
        </View>
        <View style={styles.middleSpace} />
        <TouchableHighlight
          style={styles.highlight}
          underlayColor='rgba(0,0,0,0.1)'
          onPress={_onLoginCancel}
        >
          <View style={styles.highlightInner}>
            <Image style={styles.left} source={LEFT} />
            <Text style={styles.leftText}>Back to log in</Text>
          </View>
        </TouchableHighlight>
      </View>
    );
  } else {
    content = (
      <View style={styles.box}>
        <View style={styles.topLine}>
          <View style={styles.circleView}>
            <Image style={styles.circles} source={CIRCLES} />
          </View>
          <Image style={styles.logo} source={LOGO} />
        </View>
        <Text style={styles.title}>Login to your account</Text>
        <Text style={styles.subtitle}>We will send you a unique code</Text>
        <TextInput
          style={styles.email}
          placeholder='email'
          value={email}
          onChangeText={setEmail}
          autoFocus={true}
          autoCapitalize='off'
          autoCorrect='off'
          inputMode='email'
          type='email'
          onSubmitEditing={_onSendCode}
        />
        <TextButton
          style={styles.button}
          type='blue'
          text='Send'
          onPress={_onSendCode}
        />
      </View>
    );
  }

  return (
    <View style={styles.loginScreen}>
      <View style={styles.boxView}>{content}</View>
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
